import React, { useEffect, useRef, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import "chartjs-adapter-date-fns";
import NavigateBack from "../../components/navigateBack";

import createPersistedState from "use-persisted-state";

const useTokenState = createPersistedState("token");
const usePIDState = createPersistedState("pid");
const useProfileState = createPersistedState("profile");

ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  zoomPlugin,
  Filler
);

function getLatestTime(data) {
  if (!data || data.length === 0) {
    return new Date(); // Return current date as fallback
  }

  const timestamps = data.map((d) => new Date(d.time).getTime());
  const maxTimestamp = Math.max(...timestamps);
  return new Date(maxTimestamp);
}

// Pure function to get date one year before a reference date
function getOneYearAgo(referenceDate) {
  if (!(referenceDate instanceof Date) || isNaN(referenceDate.getTime())) {
    throw new Error("Invalid reference date");
  }

  const oneYearAgo = new Date(referenceDate);
  oneYearAgo.setFullYear(referenceDate.getFullYear() - 1);
  return oneYearAgo;
}

function formatDate(isoTimestamp) {
  const date = new Date(isoTimestamp);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return "Invalid Date";
  }

  const day = date.getDate().toString().padStart(2, "0");
  const month = date.toLocaleString("default", { month: "short" });
  const year = date.getFullYear().toString().slice(-2);

  return `${day} ${month}, ${year}`;
}

function PainScoreAreaChart() {
  const chartRef = useRef(null);

  const [token, _] = useTokenState();
  const [pid, __] = usePIDState();
  const [profile, ___] = useProfileState();

  const [patientsPainScoreHistory, setPatientsPainScoreHistory] = useState();
  const [loading, setLoading] = useState(false);

  const getPatientsPainHistory = async () => {
    const URL = `/api/v1/pain-score/general/${pid}?historical=true`;
    setLoading(true);
    try {
      const response = await fetch(
        URL,

        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (data["status"]["success"]) {
        setLoading(false);
        setPatientsPainScoreHistory(data["historical"]);
      }
    } catch (error) {
      console.error("Error fetching pain history:", error);
      // Handle error appropriately, e.g., show a notification
    } finally {
      setLoading(false);
    }
  };

  const data = {
    datasets: [
      {
        label: "Pain Score",
        data: patientsPainScoreHistory?.map((item) => ({
          x: new Date(item.time),
          y: item.value,
        })),
        fill: true, // fill the area under the line
        borderColor: "#F6781B",
        backgroundColor: "rgba(246, 120, 27, 0.4)", // semi-transparent orange fill
        tension: 0.3,
        pointRadius: 5,
        borderWidth: 3,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "time",
        min:
          patientsPainScoreHistory &&
          getOneYearAgo(getLatestTime(patientsPainScoreHistory)),
        max:
          patientsPainScoreHistory && getLatestTime(patientsPainScoreHistory),
        minRange: 30 * 24 * 60 * 60 * 1000, // Minimum 30 days range
        time: {
          unit: "month",
          tooltipFormat: "dd MMM yyyy",
          displayFormats: {
            month: "MMM",
          },
        },
      },
      y: {
        min: 0,
        max: 10,

        ticks: {
          stepSize: 1,
        },
      },
    },
    plugins: {
      zoom: {
        pan: {
          enabled: true,
          mode: "x",
        },
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true,
          },
          mode: "x",
        },
      },
    },
  };

  useEffect(() => {
    getPatientsPainHistory();
  }, [pid, token]);

  useEffect(() => {
    if (chartRef.current && patientsPainScoreHistory?.length > 0) {
      const chart = chartRef.current;
      // Just reset zoom which will show all data
      setTimeout(() => {
        if (chart.resetZoom) chart.resetZoom();
      }, 100);

      // Then after a brief delay, gently pan
      setTimeout(() => {
        chart.pan({ x: -200 }, undefined, "x", { duration: 300 });
      }, 150);
    }
  }, [patientsPainScoreHistory]);

  return (
    <>
      <NavigateBack title="Pain Score Tracker" />
      {loading && (
        <div className="body_content">
          <div className="inner_body py-5">
            <div
              style={{
                margin: "auto",
                display: "flex",
                justifyContent: "center",
              }}
              id="loader"
            ></div>
          </div>
        </div>
      )}

      {!loading && (
        <div>
          <div style={{ height: 450, width: "100%", marginTop: "78px" }}>
            <Line ref={chartRef} data={data} options={options} />
          </div>
          <div
            style={{
              position: "absolute",
              top: "calc(78px + 450px)", // Below the chart
              bottom: 0, // Takes remaining space
              left: 0,
              right: 0,
              overflowY: "auto", // Makes it scrollable
            }}
          >
            {!patientsPainScoreHistory ? (
              <div className="container px-4 mt-4">
                <div className="text-center" style={{ color: "#000000B2" }}>
                  No pain score history available.
                </div>
              </div>
            ) : (
              <div className="container px-4 mt-4" style={{ height: "100%" }}>
                <div
                  className="list-group"
                  style={{ height: "100%", overflowY: "auto" }}
                >
                  {patientsPainScoreHistory?.map((item, index) => (
                    <div
                      key={index}
                      className="list-group-item border-bottom py-2 d-flex justify-content-between"
                      style={{
                        borderBottom: "1px solid #e0e0e0",
                        borderTop: "none",
                        borderLeft: "none",
                        borderRight: "none",
                        color: "#000000B2",
                        fontSize: "14px",
                        padding: "6px 0",
                      }}
                    >
                      <span>{item.value}</span>
                      <span>{formatDate(item.time)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default PainScoreAreaChart;
